import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ShieldOff, Clock, AlertTriangle, TrendingUp, Settings, Users, PoundSterling, Award, ArrowLeft, Coffee, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, startOfDay, addDays, getDay } from 'date-fns';
import VenueSettingsEditor from '@/components/admin/VenueSettingsEditor';
import StaffRotaManager from '@/components/admin/StaffRotaManager';
import ConcessionItemsManager from '@/components/admin/ConcessionItemsManager';
import TerminalSettingsPanel from '@/components/admin/TerminalSettingsPanel';
import { EXPERIENCE_DATA } from '@/components/booking/bookingDefaults';
import { useAuth } from '@/lib/AuthContext';

const SLOTS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'];
const DAYS_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function AdminVenue() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [tab, setTab] = useState('dashboard');

  const { data: settings = [] } = useQuery({
    queryKey: ['venue-settings'],
    queryFn: () => base44.entities.VenueSettings.list(),
    enabled: currentUser?.role === 'admin',
  });
  const { data: rigs = [] } = useQuery({
    queryKey: ['booking-rigs-venue'],
    queryFn: () => base44.entities.BookingRig.filter({ active: true }),
    enabled: currentUser?.role === 'admin',
  });
  const { data: users = [] } = useQuery({
    queryKey: ['admin-users-venue'],
    queryFn: () => base44.entities.User.list(),
    enabled: currentUser?.role === 'admin',
  });
  const { data: bookings = [] } = useQuery({
    queryKey: ['admin-bookings-venue'],
    queryFn: () => base44.entities.Booking.list('-date', 1000),
    enabled: currentUser?.role === 'admin',
  });
  const { data: hours = [] } = useQuery({
    queryKey: ['opening-hours'],
    queryFn: () => base44.entities.OpeningHours.list(),
    enabled: currentUser?.role === 'admin',
  });

  if (authLoading) {
    return <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const expHours = hours.find((h) => h.schedule_key === 'experience') || {};
  const s = settings[0] || {};
  const openTime = expHours.open_time || s.open_time || '09:00';
  const closeTime = expHours.close_time || s.close_time || '20:00';
  const openDays = (expHours.open_days && expHours.open_days.length ? expHours.open_days : s.open_days) || [0, 1, 2, 3, 4, 5, 6];
  const capacity = s.concurrent_capacity || rigs.length || 4;

  const activeSlots = SLOTS.filter((slot) => slot >= openTime && slot <= closeTime);

  const today = startOfDay(new Date());
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i));

  const bookedAt = (iso, slot) =>
    bookings.filter((b) => (b.date || '').slice(0, 10) === iso && b.time_slot === slot && b.status !== 'cancelled').length;

  const cellTone = (booked, cap) => {
    if (booked >= cap) return 'bg-red-500/15 text-red-300 border-red-500/30';
    if (booked >= Math.ceil(cap * 0.7)) return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    if (booked === 0) return 'bg-white/[0.03] text-white/30 border-white/5';
    return 'bg-green-500/10 text-green-300 border-green-500/20';
  };

  // Summary
  let totalBooked = 0;
  let totalCap = 0;
  let overDemandSlots = 0;
  days.forEach((d) => {
    if (!openDays.includes(getDay(d))) return;
    activeSlots.forEach((slot) => {
      const booked = bookedAt(format(d, 'yyyy-MM-dd'), slot);
      totalBooked += booked;
      totalCap += capacity;
      if (booked >= capacity) overDemandSlots += 1;
    });
  });
  const utilisation = totalCap > 0 ? Math.round((totalBooked / totalCap) * 100) : 0;

  // Revenue from rig bookings
  const validBookings = bookings.filter((b) => b.status !== 'cancelled' && b.total_price != null);
  const allTimeRevenue = validBookings.reduce((s, b) => s + (b.total_price || 0), 0);
  const nowDate = new Date();
  const monthRevenue = validBookings
    .filter((b) => {
      const d = b.date ? new Date(b.date) : null;
      return d && d.getMonth() === nowDate.getMonth() && d.getFullYear() === nowDate.getFullYear();
    })
    .reduce((s, b) => s + (b.total_price || 0), 0);
  const perExperience = {};
  validBookings.forEach((b) => {
    const k = b.experience_type || 'unknown';
    if (!perExperience[k]) perExperience[k] = { count: 0, revenue: 0 };
    perExperience[k].count += 1;
    perExperience[k].revenue += b.total_price || 0;
  });

  // Loyalty aggregation per customer
  const byEmail = {};
  bookings.forEach((b) => {
    const e = (b.customer_email || '').toLowerCase();
    if (!e) return;
    if (!byEmail[e]) byEmail[e] = { email: e, name: b.customer_name || '', qualifying: 0, redeemed: 0 };
    if ((b.status === 'confirmed' || b.status === 'completed') && !b.is_loyalty_free) byEmail[e].qualifying += 1;
    if (b.is_loyalty_free) byEmail[e].redeemed += 1;
  });
  const userEmails = new Set(users.map((u) => (u.email || '').toLowerCase()));
  const loyaltyRows = Object.values(byEmail)
    .map((r) => ({
      ...r,
      registered: userEmails.has(r.email),
      earned: Math.floor(r.qualifying / 20),
      balance: Math.max(0, Math.floor(r.qualifying / 20) - r.redeemed),
    }))
    .filter((r) => r.qualifying > 0 || r.redeemed > 0)
    .sort((a, b) => b.qualifying - a.qualifying);

  const tabs = [
    { key: 'dashboard', label: 'CAPACITY DASHBOARD', icon: TrendingUp },
    { key: 'revenue', label: 'REVENUE', icon: PoundSterling },
    { key: 'loyalty', label: 'LOYALTY', icon: Award },
    { key: 'capacity', label: 'CAPACITY', icon: Settings },
    { key: 'rota', label: 'STAFF ROTAS', icon: Users },
    { key: 'concessions', label: 'CONCESSIONS', icon: Coffee },
    { key: 'payments', label: 'PAYMENTS', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">BOOKINGS & SIM</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Venue Management</h1>
          <p className="text-white/30 text-sm mt-2 max-w-xl">
            Balance capacity and demand across the week. Set opening hours, manage staff rotas, and watch live booking utilisation.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b border-white/5 bg-[#0d0d0f] sticky top-16 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center gap-0 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`shrink-0 text-xs font-bold tracking-wider px-5 py-4 border-b-2 transition-all whitespace-nowrap flex items-center gap-2 ${
                tab === t.key ? 'border-primary text-primary' : 'border-transparent text-white/30 hover:text-white/60'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <section className="px-6 lg:px-10 py-10 max-w-6xl mx-auto">
        {tab === 'dashboard' && (
          <div>
            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 max-w-3xl">
              <div className="bg-[#111115] border border-white/5 p-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest">WEEK BOOKED</p>
                <p className="text-white font-black text-xl mt-1">{totalBooked}</p>
              </div>
              <div className="bg-[#111115] border border-white/5 p-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest">WEEK CAPACITY</p>
                <p className="text-white font-black text-xl mt-1">{totalCap}</p>
              </div>
              <div className="bg-[#111115] border border-white/5 p-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest">UTILISATION</p>
                <p className={`font-black text-xl mt-1 ${utilisation >= 85 ? 'text-red-400' : utilisation >= 60 ? 'text-amber-400' : 'text-primary'}`}>{utilisation}%</p>
              </div>
              <div className="bg-[#111115] border border-white/5 p-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest">OVER-DEMAND SLOTS</p>
                <p className={`font-black text-xl mt-1 ${overDemandSlots > 0 ? 'text-red-400' : 'text-white'}`}>{overDemandSlots}</p>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 mb-5 text-[10px] font-bold tracking-widest">
              <span className="flex items-center gap-1.5 text-white/40"><span className="w-3 h-3 bg-white/[0.05] border border-white/10" /> EMPTY</span>
              <span className="flex items-center gap-1.5 text-white/40"><span className="w-3 h-3 bg-green-500/10 border border-green-500/20" /> HEALTHY</span>
              <span className="flex items-center gap-1.5 text-white/40"><span className="w-3 h-3 bg-amber-500/15 border border-amber-500/30" /> HIGH DEMAND</span>
              <span className="flex items-center gap-1.5 text-white/40"><span className="w-3 h-3 bg-red-500/15 border border-red-500/30" /> OVER / FULL</span>
              <span className="text-white/30">CAPACITY: {capacity} PER SLOT · HOURS: {openTime}–{closeTime}</span>
            </div>

            {/* Grid */}
            <div className="overflow-x-auto border border-white/5">
              <div className="min-w-[760px]">
                {/* Header row */}
                <div className="grid grid-cols-8 bg-[#111115] border-b border-white/5">
                  <div className="px-4 py-3 text-white/30 text-[10px] font-bold tracking-widest">DAY</div>
                  {activeSlots.map((slot) => (
                    <div key={slot} className="px-2 py-3 text-white/30 text-[10px] font-bold tracking-widest text-center">{slot}</div>
                  ))}
                </div>
                {/* Day rows */}
                {days.map((d) => {
                  const dow = getDay(d);
                  const iso = format(d, 'yyyy-MM-dd');
                  const open = openDays.includes(dow);
                  return (
                    <div key={iso} className="grid grid-cols-8 border-b border-white/5">
                      <div className="px-4 py-3">
                        <p className="text-white text-xs font-bold">{format(d, 'EEE')}</p>
                        <p className="text-white/30 text-[10px]">{format(d, 'dd MMM')}</p>
                      </div>
                      {activeSlots.map((slot) => {
                        const booked = open ? bookedAt(iso, slot) : 0;
                        const tone = open ? cellTone(booked, capacity) : 'bg-white/[0.02] text-white/15 border-white/5';
                        return (
                          <div key={slot} className={`px-2 py-3 text-center border-l border-white/5 ${tone}`}>
                            {open ? (
                              <span className="text-xs font-bold">{booked}/{capacity}</span>
                            ) : (
                              <span className="text-[10px] tracking-widest">CLOSED</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-white/30 text-xs mt-4 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              Live snapshot of the next 7 days from today. Booked counts exclude cancelled sessions.
            </p>
          </div>
        )}

        {tab === 'revenue' && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 max-w-3xl">
              <div className="bg-[#111115] border border-white/5 p-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest">ALL-TIME REVENUE</p>
                <p className="text-primary font-black text-2xl mt-1">£{allTimeRevenue.toFixed(2)}</p>
              </div>
              <div className="bg-[#111115] border border-white/5 p-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest">THIS MONTH</p>
                <p className="text-white font-black text-2xl mt-1">£{monthRevenue.toFixed(2)}</p>
              </div>
              <div className="bg-[#111115] border border-white/5 p-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest">PAID SESSIONS</p>
                <p className="text-white font-black text-2xl mt-1">{validBookings.length}</p>
              </div>
            </div>
            <p className="text-primary text-[10px] font-bold tracking-widest mb-3">REVENUE BY EXPERIENCE</p>
            <div className="border border-white/5 overflow-x-auto">
              <div className="grid grid-cols-12 px-5 py-3 bg-[#111115] border-b border-white/5 min-w-[520px]">
                <p className="col-span-6 text-white/30 text-[10px] font-bold tracking-widest">EXPERIENCE</p>
                <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">SESSIONS</p>
                <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest text-right">REVENUE</p>
              </div>
              {Object.keys(perExperience).length === 0 ? (
                <p className="px-5 py-6 text-white/30 text-sm">No booking revenue yet.</p>
              ) : (
                Object.entries(perExperience)
                  .sort((a, b) => b[1].revenue - a[1].revenue)
                  .map(([k, v]) => (
                    <div key={k} className="grid grid-cols-12 px-5 py-3 border-b border-white/5 items-center min-w-[520px]">
                      <p className="col-span-6 text-white text-sm">{EXPERIENCE_DATA[k]?.name || k}</p>
                      <p className="col-span-3 text-white/50 text-sm">{v.count}</p>
                      <p className="col-span-3 text-primary font-bold text-sm text-right">£{v.revenue.toFixed(2)}</p>
                    </div>
                  ))
              )}
            </div>
            <p className="text-white/30 text-xs mt-4">Calculated from confirmed/completed rig bookings. Cancelled sessions excluded.</p>
          </div>
        )}

        {tab === 'loyalty' && (
          <div>
            <div className="mb-5 flex items-center gap-2">
              <Award className="w-4 h-4 text-primary" />
              <p className="text-white/40 text-sm">1 free session earned for every 20 qualifying sessions. Registered customers only.</p>
            </div>
            {loyaltyRows.length === 0 ? (
              <div className="text-center py-16 border border-white/5 bg-[#111115]">
                <Award className="w-10 h-10 text-white/10 mx-auto mb-4" />
                <p className="text-white/30 text-sm">No loyalty activity yet.</p>
              </div>
            ) : (
              <div className="border border-white/5 overflow-x-auto">
                <div className="grid grid-cols-12 px-5 py-3 bg-[#111115] border-b border-white/5 min-w-[640px]">
                  <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">CUSTOMER</p>
                  <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">EMAIL</p>
                  <p className="col-span-1 text-white/30 text-[10px] font-bold tracking-widest">REG</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest text-right">SESSIONS</p>
                  <p className="col-span-1 text-white/30 text-[10px] font-bold tracking-widest text-right">EARNED</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest text-right">BALANCE</p>
                </div>
                {loyaltyRows.map((r) => (
                  <div key={r.email} className="grid grid-cols-12 px-5 py-3 border-b border-white/5 items-center min-w-[640px]">
                    <p className="col-span-3 text-white text-sm truncate">{r.name || '—'}</p>
                    <p className="col-span-3 text-white/50 text-xs truncate">{r.email}</p>
                    <p className="col-span-1">
                      {r.registered ? (
                        <span className="text-[10px] font-bold tracking-widest text-green-400">YES</span>
                      ) : (
                        <span className="text-[10px] text-white/20">NO</span>
                      )}
                    </p>
                    <p className="col-span-2 text-white/50 text-sm text-right">{r.qualifying}</p>
                    <p className="col-span-1 text-white/70 text-sm text-right">{r.earned}</p>
                    <p className="col-span-2 text-right">
                      <span className="text-primary font-bold text-sm">{r.balance}</span>{' '}
                      <span className="text-white/30 text-xs">free</span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'capacity' && (
          <div className="max-w-3xl">
            <div className="mb-5 flex items-center gap-2">
              <Settings className="w-4 h-4 text-primary" />
              <p className="text-white/40 text-sm">Set session capacity and turnaround timing. Opening hours are managed on the <Link to="/admin/opening-hours" className="text-primary hover:underline">Opening Hours</Link> page.</p>
            </div>
            <VenueSettingsEditor />
          </div>
        )}

        {tab === 'rota' && (
          <div>
            <div className="mb-5 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <p className="text-white/40 text-sm">Plan weekly staff shifts so cover matches demand.</p>
            </div>
            <StaffRotaManager />
          </div>
        )}

        {tab === 'concessions' && (
          <div>
            <div className="mb-5 flex items-center gap-2">
              <Coffee className="w-4 h-4 text-primary" />
              <p className="text-white/40 text-sm">Manage the drinks, snacks and extras sold at the walk-in Point of Sale.</p>
            </div>
            <ConcessionItemsManager />
          </div>
        )}

        {tab === 'payments' && (
          <div className="max-w-3xl">
            <div className="mb-5 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-primary" />
              <p className="text-white/40 text-sm">How the venue Point of Sale takes payment, and which Square Terminal it sends sales to.</p>
            </div>
            <TerminalSettingsPanel />
          </div>
        )}
      </section>
    </div>
  );
}